import {
    IconArrowRight,
    IconFilterOff,
    IconMoodEmpty,
    IconPlus,
    IconShare,
    IconSpaces,
    IconUsersGroup,
    IconVideo,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Text } from '@fragments';
import { CommunityBanner, UserAvatar } from '@components/Community';
import { isProfileLinkable, profileLinkProps } from '@helpers/profile';
import AICircle from '@components/AICircle';
import Config from '../../../../../config';

import './styles.scss';

const MAX_STACK = 3;

function hoursLabel(t, minutes) {
    const m = Number(minutes) || 0;
    if (m < 60) return t('home.tile.contentMinutes', { value: m });
    return t('home.tile.contentHours', { value: Math.round(m / 60) });
}

/**
 * CommunitiesGrid — every community the user participates in, as the large
 * tile from the design: hero on top fading into the surface, logo + name +
 * owner line, description, two recessed wells (member avatars + total ·
 * content hours + groups) and a full-width "Abrir …" action. Three stacked
 * layers: blurred banner backdrop → mask-faded hero → content.
 *
 * `stats[id]` (groups + content minutes) loads per community after the list;
 * its numbers shimmer until it lands.
 *
 * `filtered` says the empty list is the search/category filter's doing rather
 * than the user having no communities — the two need different empty states.
 */
export default function CommunitiesGrid({ communities, stats, onOpen, onShare, filtered, onClearFilters }) {
    if (!communities.length) {
        return filtered ? <NoMatchesState onClearFilters={onClearFilters} /> : <EmptyState />;
    }

    return (
        <div className="home__communities-grid mt-2">
            {communities.map((c, i) => (
                <CommunityTile
                    key={c.id}
                    community={c}
                    stats={stats?.[c.id]}
                    index={i}
                    onOpen={onOpen}
                    onShare={onShare}
                />
            ))}
        </div>
    );
}

function CommunityTile({ community: c, stats, index, onOpen, onShare }) {
    const { t } = useTranslation('dashboard');

    const hero = c.hero_condensed || c.hero;
    const admins = c.members?.admins || [];
    const owner = admins.find(a => a.role === 'owner') || admins[0] || null;
    const membersTotal = c.members?.total ?? 0;
    const stack = admins.slice(0, MAX_STACK);

    return (
        <Card
            flat
            noBodyPadding
            className="community-tile home__rise"
            style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
        >
            {/* Layer 1 (behind everything): the banner, heavily blurred, as the
                tile's ambient backdrop. */}
            {c.banner && (
                <span
                    className="community-tile__backdrop"
                    style={{ backgroundImage: `url(${c.banner})` }}
                    aria-hidden="true"
                />
            )}

            {/* Layer 2: the hero, clipped by a mask that fades it out toward the
                bottom so the content row below appears to rise out of it. */}
            <div className="community-tile__hero" aria-hidden="true">
                {hero ? <img src={hero} alt="" /> : <IconSpaces />}
            </div>

            {/* Share sits over the hero's top-right corner, above every layer so
                the hero's fade mask can't dim it. */}
            {onShare && (
                <div className="community-tile__actions">
                    <Button
                        variant="rounded"
                        color="current"
                        size="small"
                        icon={<IconShare />}
                        tooltip={t('home.tile.shareTooltip')}
                        onClick={() => onShare(c)}
                    />
                </div>
            )}

            {/* Layer 3: content. */}
            <div className="community-tile__body">
                <div className="community-tile__identity">
                    <CommunityBanner banner={c.banner} size={72} className="community-tile__logo" />
                    <div className="community-tile__heading">
                        <Text.h3 weight={600} contrast={10} maxLines={1}>{c.name}</Text.h3>
                        {owner && (
                            <div
                                className={`community-tile__owner${isProfileLinkable(owner) ? ' community-tile__owner--link' : ''}`}
                                {...profileLinkProps(owner)}
                            >
                                <UserAvatar user={owner} size={24} link={false} />
                                <Text.span contrast={6} maxLines={1}>{owner.username}</Text.span>
                            </div>
                        )}
                    </div>
                </div>

                {c.description && (
                    <Text.p contrast={7} maxLines={2} className="community-tile__description">
                        {c.description}
                    </Text.p>
                )}

                <div className="community-tile__wells">
                    <div className="community-tile__well community-tile__well--members">
                        <div className="community-tile__stack" aria-hidden="true">
                            {stack.length ? (
                                stack.map((a, i) => (
                                    <span key={a.id || i} className="community-tile__stack-item">
                                        <UserAvatar user={a} size={30} />
                                    </span>
                                ))
                            ) : (
                                <span className="community-tile__stack-item community-tile__stack-item--empty" />
                            )}
                        </div>
                        <Text.span contrast={8}>{t('home.tile.members', { count: membersTotal })}</Text.span>
                    </div>

                    <div className="community-tile__well community-tile__well--stats">
                        <Text.span contrast={8} left={<IconVideo />} loading={!stats}>
                            {stats ? hoursLabel(t, stats.contentMinutes) : t('home.tile.contentHours', { value: 0 })}
                        </Text.span>
                        <Text.span contrast={8} left={<IconUsersGroup />} loading={!stats}>
                            {stats ? t('home.tile.groups', { count: stats.groups }) : t('home.tile.groups', { count: 0 })}
                        </Text.span>
                    </div>
                </div>

                <Button
                    color="inverse"
                    size="medium"
                    fullWidth
                    endIcon={<IconArrowRight />}
                    onClick={() => onOpen(c)}
                >
                    {t('home.tile.open', { name: c.name })}
                </Button>
            </div>
        </Card>
    );
}

// The user does have communities — the filters just don't match any of them.
function NoMatchesState({ onClearFilters }) {
    const { t } = useTranslation('dashboard');

    return (
        <Card flat className="community-tile community-tile--empty">
            <div className="community-tile__empty">
                <span className="community-tile__empty-icon"><IconMoodEmpty /></span>
                <Text.h5 weight={500} contrast={10}>{t('home.tile.noMatchesTitle')}</Text.h5>
                <Text.span contrast={6}>
                    {t('home.tile.noMatchesSubtitle')}
                </Text.span>
                <Button color="inverse" size="small" endIcon={<IconFilterOff />} onClick={onClearFilters}>
                    {t('home.tile.clearFilters')}
                </Button>
            </div>
        </Card>
    );
}

function EmptyState() {
    const { t } = useTranslation('dashboard');

    return (
        <Card flat className="community-tile community-tile--empty">
            <div className="community-tile__empty">
                <span className="community-tile__ai"><AICircle size={56} /></span>
                <Text.h5 weight={500} contrast={10}>{t('home.tile.emptyTitle')}</Text.h5>
                <Text.span contrast={6}>
                    {t('home.tile.emptySubtitle')}
                </Text.span>
                <Button
                    color="inverse"
                    size="small"
                    endIcon={<IconPlus />}
                    onClick={() => window.open(Config.communityBuilder, '_blank', 'noopener')}
                >
                    {t('home.tile.emptyAction')}
                </Button>
            </div>
        </Card>
    );
}
